import QrScanner from 'qr-scanner';
import { useEffect, useRef, useState } from 'react';
import './QRScanner.scss';

const QRScanner = ({ onScan, cameraId, onCameraError, onLoading }) => {
  const videoElementRef = useRef(null);
  const qrScannerRef = useRef(null);
  const [scanned, setScannedText] = useState('');

  // Start loading indicator if provided
  useEffect(() => {
    if (onLoading) {
      onLoading(true);
    }
  }, []);

  useEffect(() => {
    let qrScanner = null;
    
    const initScanner = async () => {
      try {
        const video = videoElementRef.current;
        
        if (!video) {
          console.error('Video element not found');
          if (onCameraError) onCameraError('Video element not found');
          return;
        }

        // Create scanner instance
        qrScanner = new QrScanner(
          video,
          (result) => {
            console.log('Decoded QR code:', result);
            setScannedText(result.data);
            
            // Call the external handler if provided
            if (onScan) {
              onScan(result.data, result);
            }
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            preferredCamera: cameraId || 'environment',
          }
        );
        
        // Store scanner instance for access in other methods
        qrScannerRef.current = qrScanner;
        
        // Start scanning
        await qrScanner.start();
        console.log('QR Scanner started');
        
        // Notify parent component that loading is complete
        if (onLoading) {
          onLoading(false);
        }
      } catch (error) {
        console.error('Error initializing QR scanner:', error);
        if (onCameraError) {
          let message = 'Failed to initialize camera';
          
          if (error.name === 'NotAllowedError' || error.message?.includes('Permission denied')) {
            message = 'Camera access denied. Please grant permission and try again.';
          } else if (error.name === 'NotFoundError' || error.message?.includes('Requested device not found')) {
            message = 'No camera found on this device.';
          }
          
          onCameraError(message);
        }
      }
    };

    initScanner();

    // Clean up function to stop and destroy scanner when component unmounts
    return () => {
      if (qrScannerRef.current) {
        console.log('Stopping and destroying QR scanner');
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
      }
    };
  }, [cameraId, onScan, onCameraError, onLoading]);

  // Method to switch camera
  const switchCamera = async (newCameraId) => {
    try {
      if (qrScannerRef.current) {
        await qrScannerRef.current.setCamera(newCameraId);
        return true;
      }
    } catch (error) {
      console.error('Error switching camera:', error);
      if (onCameraError) {
        onCameraError('Failed to switch camera');
      }
      return false;
    }
  };

  return (
    <div className="qr-scanner-container">
      <div className="video-wrapper">
        <video className="qr-video" ref={videoElementRef} />
      </div>
      {scanned && (
        <p className="scanned-text">Scanned: {scanned}</p>
      )}
    </div>
  );
};

export default QRScanner;
